import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Archive, RotateCcw, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { serviceDeliveryAPI } from '../lib/api';
import { useAuthStore } from '../store';

const fmt = (d) => (d ? new Date(`${d.slice(0, 10)}T12:00:00`).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' }) : '');

export default function ClientArchiveControls({ client, onChanged }) {
  const { user } = useAuthStore();
  const isCeo = user?.role === 'ceo';
  const [open, setOpen] = useState(false);
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [motivo, setMotivo] = useState('');
  const [busy, setBusy] = useState(false);
  const cid = client._id || client.id;
  const inactive = client.estado === 'inactivo';

  const archive = async () => {
    setBusy(true);
    try {
      const r = await serviceDeliveryAPI.archiveClient(cid, { fecha_baja: fecha, motivo });
      toast.success(`${client.nombre} dado de baja · ${r.tasks_cancelled} tareas y ${r.posts_cancelled} publicaciones canceladas`);
      setOpen(false);
      onChanged?.({ ...client, estado: 'inactivo', fecha_baja: r.fecha_baja, motivo_baja: motivo });
    } catch (e) { toast.error(e?.message || 'No se pudo dar de baja'); }
    finally { setBusy(false); }
  };
  const reactivate = async () => {
    if (!window.confirm(`¿Reactivar a ${client.nombre}? Recuperará su acceso al portal, sus tareas y sus publicaciones canceladas.`)) return;
    setBusy(true);
    try {
      const r = await serviceDeliveryAPI.reactivateClient(cid);
      toast.success(`${client.nombre} reactivado · ${r.tasks_restored} tareas y ${r.posts_restored} publicaciones recuperadas`);
      onChanged?.({ ...client, estado: 'activo', fecha_baja: null, motivo_baja: null });
    } catch (e) { toast.error(e?.message || 'No se pudo reactivar'); }
    finally { setBusy(false); }
  };

  if (inactive) {
    return (
      <div className="flex items-center gap-3 flex-wrap rounded-lg border border-slate-300 bg-slate-100 px-4 py-3" data-testid="client-inactive-banner">
        <Archive className="w-5 h-5 text-slate-600" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-800">Cliente dado de baja {client.fecha_baja && `el ${fmt(client.fecha_baja)}`}</p>
          <p className="text-xs text-slate-600">{client.motivo_baja ? `Motivo: ${client.motivo_baja}` : 'Sin motivo indicado'} · Se conserva todo su histórico. Sin acceso al portal.</p>
        </div>
        {isCeo && (
          <Button onClick={reactivate} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="client-reactivate-btn">
            {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1.5" />} Reactivar cliente
          </Button>
        )}
      </div>
    );
  }
  if (!isCeo) return null;
  return (
    <>
      <div className="flex justify-end -mt-2">
        <button onClick={() => setOpen(true)} className="text-xs text-muted-foreground hover:text-red-600 inline-flex items-center gap-1" data-testid="client-archive-btn">
          <Archive className="w-3.5 h-3.5" /> Dar de baja al cliente
        </button>
      </div>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent data-testid="client-archive-dialog">
          <DialogHeader>
            <DialogTitle>Dar de baja a {client.nombre}</DialogTitle>
            <DialogDescription>No se borra nada: su ficha, publicaciones, facturas, informes y horas se conservan. Desaparecerá del día a día, se cancelarán sus tareas abiertas y sus publicaciones no publicadas, y perderá el acceso a su portal. Puedes reactivarlo cuando quieras.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><p className="text-xs font-medium mb-1">Fecha de baja</p><Input type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} data-testid="client-archive-date" /></div>
            <div><p className="text-xs font-medium mb-1">Motivo / notas</p><Textarea rows={3} value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Ej: deja de contratar por presupuesto" data-testid="client-archive-motivo" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancelar</Button>
            <Button onClick={archive} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="client-archive-confirm">
              {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />} Dar de baja
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
